import type { ClassValue } from '@vs-shop/utils';

import { clsxm } from '@vs-shop/utils';

import { RatingColor, type RatingColorT } from './ratingColor';
import { RatingStarIcon } from './RatingStarIcon';

const STAR_COUNT = 5;
const STAR_INDEXES = Array.from({ length: STAR_COUNT }, (_, index) => index);

export type RatingStarsProps = {
  rating: number;
  starSize?: number;
  /** Colour of the filled portion. */
  fillColor?: RatingColorT;
  /**
   * Colour of the unfilled portion showing through behind it. "Track" is the
   * established name for the empty part of a meter; these stars are a readout,
   * so nothing here is "active" or "inactive" in the interactive sense.
   */
  trackColor?: RatingColorT;
  /**
   * Merged onto the root, which positions and aligns the readout as a whole.
   * It does NOT reach between the stars: see {@link RatingStars} on why their
   * spacing is fixed.
   */
  className?: ClassValue;
};

/** One row of five stars in a single colour. Both rows must render identically. */
const StarRow = ({ color, size }: { color: RatingColorT; size: number }) => (
  <>
    {STAR_INDEXES.map((index) => (
      <RatingStarIcon key={index} color={color} size={size} />
    ))}
  </>
);

/**
 * Five stars filled to the proportion a rating covers.
 *
 * Two identical rows: one in the track colour, and one in the fill colour laid
 * over it and clipped to `rating / STAR_COUNT` of the width. No star knows
 * whether it is full, partial or empty — the clip decides, which is what makes
 * the fill CONTINUOUS. 4.7 of 5 paints 94% of the row, a fraction no per-star
 * full/half/empty decision can express.
 *
 * **The spacing between stars is fixed, and deliberately not a caller's knob.**
 * The two rows have to register on each other exactly, so any gap would have to
 * apply to both — and a class merged onto the root reaches only the one it
 * lands on. A caller's `className` therefore positions and aligns the readout;
 * to change how the stars sit inside it, change this recipe.
 */
export function RatingStars({
  rating,
  starSize = 16,
  fillColor = RatingColor.Gold,
  trackColor = RatingColor.Inactive,
  className,
}: RatingStarsProps) {
  // Out-of-range ratings clamp rather than overflow the row or invert the clip.
  const filledPercentage = (Math.min(Math.max(rating, 0), STAR_COUNT) / STAR_COUNT) * 100;

  return (
    <div data-slot="rating-stars" className={clsxm('flex items-center', className)}>
      {/*
       * The clip below is a percentage of THIS row, not of the root, so a
       * caller sizing or aligning the root cannot move the fill off the stars
       * it belongs to.
       */}
      <div data-slot="rating-stars-track" className="relative flex items-center">
        <StarRow color={trackColor} size={starSize} />

        {/*
         * The same row again, clipped. `aria-hidden` because it duplicates what
         * is already painted below it: the pair is one readout, and a caller
         * labelling the root must not have the stars counted twice.
         */}
        <div
          data-slot="rating-stars-fill"
          aria-hidden
          className="absolute inset-y-0 left-0 overflow-hidden"
          style={{ width: `${filledPercentage}%` }}
        >
          {/*
           * `w-max`: the row keeps its whole five-star width and is CUT by the
           * box above, rather than being sized by it. Without it the row takes
           * the clip's width and only its children's `shrink-0` keeps the stars
           * from narrowing — correct by accident, and silently wrong the day a
           * star stops declaring it.
           */}
          <div data-slot="rating-stars-fill-row" className="flex h-full w-max items-center">
            <StarRow color={fillColor} size={starSize} />
          </div>
        </div>
      </div>
    </div>
  );
}
